import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import AdminUserTable from './components/AdminUserTable';
import CreateUserForm from './components/CreateUserForm';
import SecurityAuditLog from './components/SecurityAuditLog';
import ActiveSessionsPanel from './components/ActiveSessionsPanel';

const AdminCredentialsManagement = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [stats, setStats] = useState({
    totalAdmins: 0,
    activeUsers: 0,
    totalAgents: 0,
    totalOperators: 0
  });

  // Check if current user is admin
  const isAdmin = userProfile?.role === 'admin';

  useEffect(() => {
    if (!authLoading && user) {
      if (isAdmin) {
        loadUsers();
      }
    }
  }, [user, userProfile, authLoading, isAdmin]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all user profiles
      const { data, error: fetchError } = await supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setUsers(data || []);

      // Calculate stats
      const statsData = {
        totalAdmins: data?.filter(u => u?.role === 'admin')?.length || 0,
        activeUsers: data?.filter(u => u?.is_active)?.length || 0,
        totalAgents: data?.filter(u => u?.role === 'agent')?.length || 0,
        totalOperators: data?.filter(u => u?.role === 'operator')?.length || 0
      };
      setStats(statsData);

    } catch (err) {
      setError(err?.message || 'Failed to load users');
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (userData) => {
    try {
      setError(null);

      // Create auth user first
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email: userData?.email,
        password: userData?.password,
        options: {
          data: {
            full_name: userData?.fullName
          }
        }
      });

      if (authError) throw authError;

      // Update user profile with role
      if (authData?.user) {
        const { error: profileError } = await supabase?.from('user_profiles')?.update({
            role: userData?.role,
            full_name: userData?.fullName,
            is_active: true
          })?.eq('id', authData?.user?.id);

        if (profileError) throw profileError;
      }

      // Reload users
      await loadUsers();
      setShowCreateForm(false);

      return { success: true, message: 'User created successfully' };
    } catch (err) {
      setError(err?.message || 'Failed to create user');
      console.error('Error creating user:', err);
      return { success: false, error: err?.message };
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      setError(null);

      const { error: updateError } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', userId);

      if (updateError) throw updateError;

      await loadUsers();
      return { success: true, message: 'User updated successfully' };
    } catch (err) {
      setError(err?.message || 'Failed to update user');
      console.error('Error updating user:', err);
      return { success: false, error: err?.message };
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      setError(null);

      // Soft delete - just deactivate
      const { error: updateError } = await supabase?.from('user_profiles')?.update({ is_active: false })?.eq('id', userId);

      if (updateError) throw updateError;

      await loadUsers();
      return { success: true, message: 'User deactivated successfully' };
    } catch (err) {
      setError(err?.message || 'Failed to deactivate user');
      console.error('Error deactivating user:', err);
      return { success: false, error: err?.message };
    }
  };

  // Show loading state
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
            <Icon name="Shield" size={24} color="white" />
          </div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Check authentication
  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-8">
          <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Lock" size={32} color="var(--color-error)" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">
            Authentication Required
          </h2>
          <p className="text-muted-foreground mb-6">
            Please log in to access the admin credentials management system.
          </p>
          <a
            href="/admin-login"
            className="inline-flex items-center space-x-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Icon name="LogIn" size={20} />
            <span>Go to Login</span>
          </a>
        </div>
      </div>
    );
  }

  // Check admin authorization
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-8">
          <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="ShieldAlert" size={32} color="var(--color-error)" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">
            Access Denied
          </h2>
          <p className="text-muted-foreground mb-6">
            You do not have permission to access this page. Administrator privileges are required.
          </p>
          <a
            href="/database-management"
            className="inline-flex items-center space-x-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Icon name="ArrowLeft" size={20} />
            <span>Back to Dashboard</span>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Admin Credentials Management
                </h1>
                <p className="text-muted-foreground">
                  Manage administrative users, roles, and security settings
                </p>
              </div>
              
              <button
                onClick={() => setShowCreateForm(true)}
                className="flex items-center space-x-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Icon name="UserPlus" size={20} />
                <span>Create New User</span>
              </button>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={20} color="var(--color-error)" />
                <span className="text-sm font-medium text-error">{error}</span>
              </div>
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Total Admins</p>
                  <p className="text-3xl font-bold text-foreground">{stats?.totalAdmins}</p>
                </div>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Shield" size={24} color="var(--color-primary)" />
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Active Users</p>
                  <p className="text-3xl font-bold text-foreground">{stats?.activeUsers}</p>
                </div>
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="Users" size={24} color="var(--color-accent)" />
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Agents</p>
                  <p className="text-3xl font-bold text-foreground">{stats?.totalAgents}</p>
                </div>
                <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
                  <Icon name="Briefcase" size={24} color="#3b82f6" />
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Operators</p>
                  <p className="text-3xl font-bold text-foreground">{stats?.totalOperators}</p>
                </div>
                <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center">
                  <Icon name="Settings" size={24} color="#a855f7" />
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Users Table */}
            <div className="lg:col-span-2">
              <AdminUserTable
                users={users}
                onUpdate={handleUpdateUser}
                onDelete={handleDeleteUser}
                onSelectUser={setSelectedUser}
                loading={loading}
              />
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <ActiveSessionsPanel />
              <SecurityAuditLog />
            </div>
          </div>
        </div>
      </main>

      {/* Create User Modal */}
      {showCreateForm && (
        <CreateUserForm
          onClose={() => setShowCreateForm(false)}
          onSubmit={handleCreateUser}
        />
      )}
    </div>
  );
};

export default AdminCredentialsManagement;